import React, { ChangeEvent, ComponentProps } from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogProps,
    FormLabel,
    TextField,
    // FormHelperText,
    IconButton,
} from '@mui/material'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import { ExclamationCircleIcon, XIcon } from 'components/Icons'

export const PrimaryActionButton = (props: ComponentProps<typeof Button>) => {
    const { clientTheme } = useThemeStyling()

    return (
        <Button
            size="small"
            variant="contained"
            color="secondary"
            sx={(theme) => ({
                fontSize: clientTheme.typography.body1.fontSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing(1),
            })}
            {...props}
        />
    )
}

export const SecondaryActionButton = (props: ComponentProps<typeof Button>) => {
    const { clientTheme } = useThemeStyling()

    return (
        <Button
            size="small"
            variant="contained"
            sx={(theme) => ({
                ...clientTheme.buttons.defaultButton.style,
                fontSize: clientTheme.typography.body1.fontSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing(1),
            })}
            {...props}
        />
    )
}

interface CaseReivewDialogProps
    extends Omit<DialogProps, 'title' | 'onSubmit' | 'onClose'> {
    title: React.ReactNode
    primaryButton: {
        text: string
        onClick: () => void
        disabled?: boolean
    }
    secondaryButton: {
        text: string
        onClick: () => void
        disabled?: boolean
    }
}

export const CaseReivewDialog = ({
    title,
    children,
    primaryButton,
    secondaryButton,
    ...props
}: CaseReivewDialogProps) => (
    <Dialog fullWidth maxWidth="sm" {...props}>
        <DialogContent>
            {typeof title === 'string' ? (
                <h2 className="emp-modal-dialogTitle">{title}</h2>
            ) : (
                title
            )}

            {children}

            <Box
                sx={(theme) => ({
                    display: 'flex',
                    gap: theme.spacing(1),
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                })}
            >
                <SecondaryActionButton onClick={secondaryButton.onClick}>
                    {secondaryButton.text}
                </SecondaryActionButton>

                <PrimaryActionButton
                    onClick={primaryButton.onClick}
                    disabled={primaryButton.disabled}
                >
                    {primaryButton.text}
                </PrimaryActionButton>
            </Box>
        </DialogContent>

        <IconButton
            onClick={secondaryButton.onClick}
            sx={(theme) => ({
                position: 'absolute',
                top: theme.spacing(1),
                right: theme.spacing(1),
                color: 'text.secondary',
                '&:hover': {
                    backgroundColor: 'action.hover',
                },
            })}
            size="small"
        >
            <XIcon />
        </IconButton>
    </Dialog>
)

export const CaseComments = ({
    required = false,
    // error,
    onChange,
}: {
    required?: boolean
    error?: string
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}) => {
    const { clientTheme } = useThemeStyling()
    const stylesObj = {
        textInputMultiField: {
            borderRadius: clientTheme.selectionBox.borderRadius,
            '& .MuiOutlinedInput-root': {
                borderRadius: clientTheme.selectionBox.borderRadius,
                padding: '7px',
            },
            '& .MuiInputBase-input': {
                color: clientTheme.typography.fontColor.primaryText,
                resize: 'vertical',
            },
        },
    }
    return (
        <Box
            sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(1),
                marginBottom: theme.spacing(2),
            })}
        >
            <FormLabel
                sx={{ fontWeight: '600', textAlign: 'left' }}
                required={required}
            >
                Case Comments
            </FormLabel>

            <TextField
                variant="outlined"
                sx={stylesObj.textInputMultiField}
                onChange={onChange}
                placeholder="Add case comments"
                multiline
                rows={4}
            />
        </Box>
    )
}

export const ConfirmationModal = ({
    open,
    title,
    message,
    primaryButton,
    secondaryButton,
}: {
    open: boolean
    title: string
    message: string
    primaryButton: {
        text: string
        onClick: () => void
        disabled?: boolean
    }
    secondaryButton: {
        text: string
        onClick: () => void
        disabled?: boolean
    }
}) => {
    return (
        <CaseReivewDialog
            maxWidth="xs"
            open={open}
            title={
                <>
                    <ExclamationCircleIcon />
                    <h2
                        className={'emp-modal-dialogTitle'}
                        style={{ marginTop: 0 }}
                    >
                        {title}
                    </h2>
                </>
            }
            primaryButton={primaryButton}
            secondaryButton={secondaryButton}
        >
            <p>{message}.</p>
        </CaseReivewDialog>
    )
}
